<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-metrics-page" v-loading="loading" element-loading-text="Loading...">
    <div>
      <div class="stats-header">
        <span>
          <div class="wy-page-title-small">{{$t(currentPageInfo.subtitle)}}</div>
          <div class="wy-page-title">{{$t(currentPageInfo.title)}}
            <wy-page-tooltip />
          </div>
        </span>
      </div>
      <div v-if="hasData" class="stats-summary-zoom-in-on" v-show="!loading">
        <wy-analytics-metrics-zoom-in-on :data="zoomInData"/>
      </div>
      <no-data-page v-else> </no-data-page>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import WyAnalyticsMetricsZoomInOn from './wy-analytics-metrics-zoom-in-on';
import wyPageTooltip from '../widgets/wy-page-tooltip.vue';
import noDataPage from '../../views/PageNoDataWithAvailableDateRange';
import WyUtils from '../../library/wy-utils';

export default Vue.extend({
  name: 'wy-analytics-metrics-page',
  components: {
    'wy-analytics-metrics-zoom-in-on': WyAnalyticsMetricsZoomInOn,
    'wy-page-tooltip': wyPageTooltip,
    'no-data-page': noDataPage
  },

  data() {
    return {
      hasData: true,
      loading: false,
      zoomInData: null,
      initDate: null
    }
  },

  computed: {
    ...mapGetters(['dataModel', 'transfersVisible', 'containmentVisible', 'config', 'currentPageInfo','isLoggedIn']),
  },

  methods: {
    ...mapActions(['fetchMetrics']),

    async handleFilterChange() {
      if((!this.loading && this.dataModel.dateEnd) || this.dataModel.dateEnd) {
        this.loading = true;

        let res = await this.fetchMetrics();
        if(res.status == 200) {
          const xnumber = this.$store.getters.xnumber;
          this.zoomInData = res.data;
          this.zoomInData.totalVolumeDMValue = (this.zoomInData.totalVolume!=null ? this.zoomInData.totalVolume * xnumber : this.zoomInData.totalVolume);
          this.zoomInData.compareVolumeDMValue = (this.zoomInData.compareVolume!=null ? this.zoomInData.compareVolume * xnumber : this.zoomInData.compareVolume);
          this.zoomInData.volumeDMValueMedian = (this.zoomInData.volumeMedian!=null ? this.zoomInData.volumeMedian * xnumber : this.zoomInData.volumeMedian);
          this.zoomInData.trend.forEach(item => {
            item.volumeDMValue = (item.volume!=null ? item.volume * xnumber : item.volume);
            item.volumeDMValueRegression = (item.volumeRegression!=null ? item.volumeRegression * xnumber : item.volumeRegression);
          })
        }

        // Providing information for hiding certain charts in response to changes in global filters
        this.zoomInData.containmentPercentHide = !this.containmentVisible;
        this.zoomInData.avgTransfersHide = !this.transfersVisible;
        this.loading = false;
        this.hasData = (this.zoomInData.trend && this.zoomInData.trend.length > 0);
      }
    },

    refreshPage() {
      this.handleFilterChange();
    }
  },

  async created() {
    this.initDate = new Date()
    this.$router.currPageComponent = this;
    // listen events from other components
    this.$root.registerPageEvent(this.$route.name, (event) => {
      if(this[event.name]) this[event.name]();
    })
    this.handleFilterChange();

    // send Amplitude events
    this.$store.dispatch('amplitudeLogWithFilters', {name: 'aggr_metrics_page_loaded', data: {}});
  },

  mounted() {
    this.$root.registerTooltips(this.$el);
  },

  beforeDestroy() {
    this.$root.unregisterTooltips(this.$el);
  },

  destroyed() {
    if(this.isLoggedIn) {
      this.$store.dispatch('amplitudeLogWithFilters', {name: 'aggregated_metrics_page_time_spent', data: {time_spent: WyUtils.getTimeDiff(this.initDate)}});
    }
  }

});
</script>

<style lang="scss">
  .wy-analytics-metrics-page {
    .el-loading-mask {
      height: calc(100vh - 100px) !important;
    }
    .wy-page-title {
      margin-bottom: 40px;
      line-height: 0.7;
      height: 25px;
    }
    .wy-page-title-small {
      display: none;
      color: var(--site-font-secondary-color);
      font-size: 15px;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      font-weight: 400;
    }
    .el-loading-text {
      display: none;
    }

    min-width: 1130px;
    .stats-header {
      color: var(--site-font-secondary-color);
      font-size: 25px;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      font-weight: 600;
      .el-icon-info {
        position: relative;
        left: 5px;
        bottom: 10px;
        line-height: 0;
        background-color: #ACB8C6;
        color: #214586;
        font-size: 15px;
        cursor: pointer;
      }
    }
    .stats-summary-zoom-in-on {
      margin-top: 20px;
    }
  }

</style>